import { useCallback, useEffect, useState } from 'react';
import { CaseBriefOverlay } from './overlay/CaseBriefOverlay.jsx';
import { CaseStudyOverlay } from './overlay/CaseStudyOverlay.jsx';
import { ExperienceOverlay } from './overlay/ExperienceOverlay.jsx';
import { InkRevealCursor } from './components/InkRevealCursor.jsx';
import { FlatMode } from './modes/FlatMode.jsx';
import { JourneyMode } from './modes/JourneyMode.jsx';
import { SpaceCanvas } from './scene/SpaceCanvas.jsx';
import { breakdownContent } from './content/breakdownContent.js';
import { portfolioContent } from './content/portfolioContent.js';
import { BEAT_COUNT, DWELL } from './scene/journeyPath.js';
import { getLenis, initLenis, scrollToTarget, scrollToTop } from './scroll/scrollStore.js';
import { usePerfTier } from './scene/perfTier.js';

const ORIGINAL_TITLE = 'Priyanka Vasandiya — Product & Program Management';

// Case-study views, hash-addressable (PM-site parity, owner-approved
// 2026-07-12): the brief is the default reading layer (#/work/<case>), the
// full breakdown one level deeper. The old #/solar-breakdown hash stays
// alive as a legacy alias so previously shared links keep working.
const ROUTES = {
  '#/work/solar': { view: 'brief', caseKey: 'solar' },
  '#/work/copilot': { view: 'brief', caseKey: 'copilot' },
  '#/work/solar/breakdown': { view: 'breakdown' },
  '#/solar-breakdown': { view: 'breakdown' }, // legacy alias
  '#/experience': { view: 'experience' },
};
const routedHash = (hash) => (ROUTES[hash] ? hash : null);

const CASE_INDEX = { solar: 0, copilot: 1 };

export function App() {
  const perfTier = usePerfTier();
  const journeyAllowed = useJourneyAvailability();
  const [mode, setMode] = useState('flat');
  const [routeHash, setRouteHash] = useState(() => routedHash(window.location.hash));
  const route = routeHash ? ROUTES[routeHash] : null;
  const overlayOpen = route !== null;

  useEffect(() => {
    initLenis();
  }, []);

  useEffect(() => {
    if (!journeyAllowed && mode !== 'flat') setMode('flat');
  }, [journeyAllowed, mode]);

  // Hash routing, ported from the original two-view pattern: pushState on link
  // clicks (so the instant view swap runs, not native anchor scroll),
  // hashchange covers back/forward, initial state covers deep links.
  useEffect(() => {
    function routeFromHash() {
      setRouteHash(routedHash(window.location.hash));
    }
    window.addEventListener('hashchange', routeFromHash);
    return () => window.removeEventListener('hashchange', routeFromHash);
  }, []);

  useEffect(() => {
    if (!route) {
      document.title = ORIGINAL_TITLE;
      return;
    }
    document.title =
      route.view === 'breakdown'
        ? breakdownContent.docTitle
        : route.view === 'experience'
          ? `${portfolioContent.experience.roleTitle} — Experience`
          : `${portfolioContent.work.cases[CASE_INDEX[route.caseKey]].title} — Case Brief`;
    requestAnimationFrame(() => scrollToTop());
    // routeHash (not route) so brief→breakdown transitions also scroll to top
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeHash]);

  const openView = useCallback((hash) => {
    history.pushState(null, '', hash);
    setRouteHash(hash);
  }, []);

  // Closing a view targets a flat section anchor — but JourneyMode has no
  // section anchors, so there we jump to the corresponding beat instead
  // (previously this no-opped and silently restarted the journey at the hero).
  const closeView = useCallback(
    (targetId = 'work', journeyBeat = null) => {
      history.pushState(null, '', `#${targetId}`);
      setRouteHash(null);
      requestAnimationFrame(() => {
        // The short overlay just unmounted and the tall view remounted, but
        // Lenis's dimensions refresh is debounced (250ms) — scrollTo clamps
        // against the STALE limit and strands the user near the top. Force a
        // synchronous re-measure before any immediate jump.
        const lenis = getLenis();
        if (lenis) lenis.resize();
        if (journeyBeat != null && journeyAllowed && mode === 'journey') {
          const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
          const target = ((journeyBeat + DWELL * 0.55) / BEAT_COUNT) * max;
          if (lenis) lenis.scrollTo(target, { immediate: true });
          else window.scrollTo(0, target);
          // keyboard focus parity with the flat branch — the overlay that had
          // focus is gone; anchor focus in the journey shell
          const shell = document.querySelector('.journey-shell');
          if (shell) {
            shell.setAttribute('tabindex', '-1');
            shell.focus({ preventScroll: true });
          }
          return;
        }
        scrollToTarget(`#${targetId}`, { immediate: true });
        const target = document.getElementById(targetId);
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      });
    },
    [journeyAllowed, mode],
  );

  const activeMode = journeyAllowed ? mode : 'flat';

  function handleModeChange(nextMode) {
    setMode(nextMode);
    scrollToTop();
  }

  return (
    <main className={`app-shell mode-${activeMode}`}>
      <SpaceCanvas perfTier={perfTier} mode={overlayOpen ? 'flat' : activeMode} />
      {activeMode === 'flat' && !overlayOpen && <InkRevealCursor />}
      {journeyAllowed && !overlayOpen && (
        <div className="mode-toggle" aria-label="Portfolio mode">
          <button type="button" className={activeMode === 'flat' ? 'active' : ''} onClick={() => handleModeChange('flat')}>
            Flat
          </button>
          <button type="button" className={activeMode === 'journey' ? 'active' : ''} onClick={() => handleModeChange('journey')}>
            Journey
          </button>
        </div>
      )}
      {overlayOpen ? (
        route.view === 'breakdown' ? (
          <CaseStudyOverlay onBrief={() => openView('#/work/solar')} onContact={() => closeView('contact', 7)} />
        ) : route.view === 'experience' ? (
          <ExperienceOverlay onBack={() => closeView('experience', 4)} onContact={() => closeView('contact', 7)} />
        ) : (
          <CaseBriefOverlay
            caseKey={route.caseKey}
            onBack={() => closeView('work', route.caseKey === 'copilot' ? 3 : 2)}
            onBreakdown={() => openView('#/work/solar/breakdown')}
            onContact={() => closeView('contact', 7)}
          />
        )
      ) : activeMode === 'journey' ? (
        <JourneyMode
          onOpenCase={(key) =>
            openView(key === 'copilot' ? '#/work/copilot' : key === 'experience' ? '#/experience' : '#/work/solar')
          }
        />
      ) : (
        <FlatMode
          journeyAllowed={journeyAllowed}
          onJourney={() => handleModeChange('journey')}
          onBreakdown={() => openView('#/work/solar/breakdown')}
          overlayOpen={overlayOpen}
        />
      )}
    </main>
  );
}

function useJourneyAvailability() {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 900px) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
    const sync = () => setAllowed(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return allowed;
}
